import { useEffect, useState, useCallback, use } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Card,
  CardHeader,
  Heading,
  HStack,
  Button,
  useToast,
  InputGroup,
  InputLeftElement,
  Input,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { WSAuthentication } from "../../../services/remote/authentication/WSAuthentication";
import { User } from "../../../entity/User";
import { setLoading } from "../../../store/slices/LoadingSlice";
import { UsersTable } from "./components/UsersTable";
import { UserFormModal } from "./components/UserFormModal";
import Dialog from "../../components/Dialog";
import { SearchIcon } from "@chakra-ui/icons";
import { SearchUserModal } from "./components/SearchUserModal";

export const UsersManagement = () => {
  const authService = WSAuthentication.getInstance();

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [userIdToDelete, setUserIdToDelete] = useState<string>("");
  const [searchEmail, setSearchEmail] = useState("");
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const toast = useToast();
  const loadUsers = useCallback(async () => {
    try {
      const users = await authService.getAllUsers();
      setUsers(users.first);
    } catch (error) {
      toast({
        title: t("login.loadError"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, toast, t]);

  const handleSearch = useCallback(
    (email: string, options: { isActive?: boolean }) => {
      const localUser = users.filter((user) => user.email == email);
      if (localUser.length > 0) {
        setUsers(localUser);
      } else {
        authService.getUserByEmail(email).then((user) => {
          if (user) {
            setUsers([user]);
          } else {
            setUsers([]);
          }
        });
      }
    },
    [],
  );

  useEffect(() => {
    loadUsers();
  }, []);

  const handleUserCreated = (newUser: User) => {
    setUsers((prev) => [...prev, newUser]);
  };

  const handleUserUpdated = (updatedUser: User) => {
    setUsers((prev) =>
      prev.map((user) => (user.id === updatedUser.id ? updatedUser : user)),
    );
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (userId: string) => {
    setUserIdToDelete(userId);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!userIdToDelete) return;

    try {
      dispatch(setLoading(true));
      const authService = WSAuthentication.getInstance();
      await authService.deleteUser(userIdToDelete);

      setUsers((prev) => prev.filter((user) => user.id !== userIdToDelete));
      setIsDeleteDialogOpen(false);

      toast({
        title: t("login.deleteSuccess"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Failed to delete user:", error);
      toast({
        title: t("login.deleteError"),
        description: error instanceof Error ? error.message : "Unknown error",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <Box p={4}>
      <Card variant="outline" mb={6}>
        <CardHeader>
          <HStack justify="space-between" align="center">
            <Heading size="md">{t("login.manageUsers")}</Heading>
            <HStack spacing={4}>
              <HStack spacing={4}>
                <Button
                  leftIcon={<SearchIcon />}
                  onClick={() => setIsSearchModalOpen(true)}
                >
                  {t("login.search")}
                </Button>
                <Button
                  onClick={() => {
                    setSelectedUser(null);
                    setIsModalOpen(true);
                  }}
                >
                  {t("login.createUser")}
                </Button>
              </HStack>
              <SearchUserModal
                isOpen={isSearchModalOpen}
                onClose={() => setIsSearchModalOpen(false)}
                onSearch={handleSearch}
              />
            </HStack>
          </HStack>
        </CardHeader>
        <Box p={4}>
          <UsersTable
            users={users}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
          />
        </Box>
      </Card>

      <UserFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setSelectedUser(null);
          setIsModalOpen(false);
        }}
        onSuccess={selectedUser ? handleUserUpdated : handleUserCreated}
        userToEdit={selectedUser}
      />

      <Dialog
        onClick={handleDelete}
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title={t("popup.txt_delete_user")}
        description={t("popup.are_you_sure_delete_user")}
        positiveButtonText={t("popup.txt_delete")}
        negativeButtonText={t("popup.txt_cancel")}
      />
    </Box>
  );
};
